import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Upload, FileText, Loader2, Trash2, Eye, ArrowLeft,
  User, DollarSign, AlertTriangle, TrendingDown, BarChart3,
  RefreshCw, Download
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DECISION_COLORS = {
  APPROVE: 'bg-emerald-600 text-white',
  MANUAL_REVIEW: 'bg-amber-500 text-white',
  DECLINE: 'bg-red-600 text-white',
};

const RISK_BADGE = {
  low: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-red-100 text-red-800',
  unknown: 'bg-gray-100 text-gray-800',
};

export default function BankVerification({ getAuthHeader }) {
  const [view, setView] = useState('list');
  const [statements, setStatements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [robotData, setRobotData] = useState(null);
  const [robotLoading, setRobotLoading] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [bankName, setBankName] = useState('');

  const fetchStatements = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/bank-verification/statements`, getAuthHeader());
      setStatements(res.data);
    } catch { /* ignore */ }
  }, [getAuthHeader]);

  useEffect(() => { fetchStatements(); }, [fetchStatements]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const fileInput = e.target.querySelector('input[type="file"]');
    const file = fileInput?.files?.[0];
    if (!file) { toast.error('Select a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('customer_name', customerName);
      fd.append('bank_name', bankName);
      const res = await axios.post(`${API_URL}/bank-verification/upload`, fd, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Uploaded');
      setSelectedId(res.data.statement_id);
      setCustomerName('');
      setBankName('');
      fileInput.value = '';
      fetchStatements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const processStatement = async (id) => {
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/bank-verification/${id}/process`, {}, getAuthHeader());
      toast.success('Processed');
      fetchStatements();
      loadRobot(id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Processing failed');
    } finally { setProcessing(false); }
  };

  const loadRobot = async (id) => {
    setRobotLoading(true);
    try {
      const res = await axios.post(`${API_URL}/bank-verification/${id}/robot-analysis`, {}, getAuthHeader());
      setRobotData(res.data);
    } catch {
      setRobotData(null);
    } finally { setRobotLoading(false); }
  };

  const refreshRobot = async (id) => {
    setRobotLoading(true);
    try {
      const res = await axios.post(`${API_URL}/bank-verification/${id}/robot-analysis`, {}, getAuthHeader());
      setRobotData(res.data);
      toast.success('Report refreshed');
    } catch { toast.error('Refresh failed'); }
    finally { setRobotLoading(false); }
  };

  const openReport = (stmt) => {
    setSelectedId(stmt.id);
    loadRobot(stmt.id);
    setView('report');
  };

  const deleteStatement = async (id) => {
    if (!window.confirm('Delete this statement?')) return;
    try {
      await axios.delete(`${API_URL}/bank-verification/${id}`, getAuthHeader());
      toast.success('Deleted');
      fetchStatements();
      if (selectedId === id) { setView('list'); setSelectedId(null); setRobotData(null); }
    } catch (err) { toast.error('Delete failed'); }
  };

  const downloadReport = () => {
    if (!robotData || !selectedId) return;
    const simple = robotData.simple_report || {};
    const trend = robotData.monthly_trend || [];
    const mie = robotData.monthly_income_expense || {};
    const lenders = robotData.monthly_lenders || [];
    const risk = robotData.risk_summary || {};

    const ident = simple.identity || {};
    let text = `BANK STATEMENT ANALYSIS REPORT\n${'='.repeat(50)}\n\n`;
    text += `Customer: ${ident.full_name || 'N/A'}\n`;
    text += `Address: ${ident.address || 'N/A'}\n\n`;
    text += `DECISION: ${simple.affordability?.decision || 'N/A'}\n`;
    text += `Safe to Lend: R ${simple.affordability?.safe_borrow_amount || 0}\n`;
    text += `Risk Score: ${risk.risk_score || 0}/100\n\n`;
    text += `${'─'.repeat(50)}\nKEY METRICS\n${'─'.repeat(50)}\n`;
    text += `Salary: R ${simple.salary?.main_salary_amount || 0} (${simple.salary?.salary_frequency || '?'})\n`;
    text += `Lenders: ${simple.lender_detection?.count || 0} (Repayments: R ${simple.lender_detection?.total_repayments || 0})\n`;
    text += `Gambling: ${simple.gambling?.count || 0} (Spend: R ${simple.gambling?.total_spend || 0})\n`;
    text += `Avg Balance: R ${simple.balances?.average_balance || 0}\n`;
    text += `Lowest Balance: R ${simple.balances?.lowest_balance || 0}\n\n`;

    // Income table
    const months = mie.months || [];
    const avg = mie.average || {};
    if (months.length > 0) {
      text += `${'─'.repeat(50)}\nINCOME BREAKDOWN\n${'─'.repeat(50)}\n`;
      const header = 'Category'.padEnd(40) + months.map(m => m.month.padStart(14)).join('') + 'Average'.padStart(14) + '\n';
      text += header;
      const incomeRows = [
        ['Gross Salary', 'fixed_monthly_gross_salary'],
        ['Overtime', 'average_monthly_overtime'],
        ['Other Income', 'other_monthly_income'],
        ['Family Contribution', 'contribution_from_family_member'],
        ['Spouse Income', 'spouse_income'],
      ];
      for (const [label, key] of incomeRows) {
        text += label.padEnd(40);
        for (const m of months) text += String(m.income?.[key]?.toFixed(2) || '0.00').padStart(14);
        text += String(avg.income?.[key]?.toFixed(2) || '0.00').padStart(14) + '\n';
      }
      text += 'GROSS TOTAL'.padEnd(40);
      for (const m of months) text += String(m.gross_monthly_income?.toFixed(2) || '0.00').padStart(14);
      text += String(avg.gross_monthly_income?.toFixed(2) || '0.00').padStart(14) + '\n';
      text += 'TAKE HOME'.padEnd(40);
      for (const m of months) text += String(m.total_take_home_income?.toFixed(2) || '0.00').padStart(14);
      text += String(avg.total_take_home_income?.toFixed(2) || '0.00').padStart(14) + '\n\n';

      text += `${'─'.repeat(50)}\nEXPENSE BREAKDOWN\n${'─'.repeat(50)}\n`;
      const expenseLabels = {
        property_rental: 'Property Rental', utilities_water_electricity: 'Utilities',
        groceries: 'Groceries', transport_fuel: 'Transport/Fuel',
        communication_airtime_data_wifi: 'Communication', school_fees: 'School Fees',
        childcare_creche: 'Childcare', medical_aid: 'Medical Aid',
        medication: 'Medication', debt_repayments_not_listed_on_credit_bureau: 'Debt Repayments',
        insurance_funeral_car_household: 'Insurance', entertainment: 'Entertainment',
        clothing_footwear: 'Clothing', savings_stokvel_contributions: 'Savings/Stokvel',
        household_maintenance: 'Maintenance', other_expense: 'Other',
      };
      for (const [key, label] of Object.entries(expenseLabels)) {
        text += label.padEnd(40);
        for (const m of months) text += String(m.expenses?.[key]?.toFixed(2) || '0.00').padStart(14);
        text += String(avg.expenses?.[key]?.toFixed(2) || '0.00').padStart(14) + '\n';
      }
      text += 'TOTAL EXPENSES'.padEnd(40);
      for (const m of months) text += String(m.total_expenses?.toFixed(2) || '0.00').padStart(14);
      text += String(avg.total_expenses?.toFixed(2) || '0.00').padStart(14) + '\n\n';
    }

    // Lender activity
    if (lenders.length > 0) {
      text += `${'─'.repeat(50)}\nLENDER ACTIVITY BY MONTH\n${'─'.repeat(50)}\n`;
      for (const m of lenders) {
        text += `\n${m.month}: Gave R${m.total_gave}, Took R${m.total_took}\n`;
        for (const l of m.lenders) {
          text += `  ${l.name.padEnd(30)} Gave: R${l.gave}  Took: R${l.took}  (${l.transaction_count} txns)\n`;
        }
      }
      text += '\n';
    }

    // Salary trend
    if (trend.length > 0) {
      text += `${'─'.repeat(50)}\nSALARY TREND\n${'─'.repeat(50)}\n`;
      for (const t of trend) {
        text += `${t.month}: R ${t.salary} (${t.salary_deposits} deposits)\n`;
      }
    }

    text += `\n${'='.repeat(50)}\nGenerated: ${new Date().toISOString().slice(0,10)}\n`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${ident.full_name?.replace(/\s+/g, '_') || selectedId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (v) => v != null ? `R ${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : 'R 0.00';

  // ============ REPORT VIEW ============
  if (view === 'report' && selectedId) {
    const simple = robotData?.simple_report;
    const trend = robotData?.monthly_trend || [];
    const stmt = statements.find(s => s.id === selectedId);
    const riskSummary = robotData?.risk_summary;
    const gambling = robotData?.gambling_patterns;
    const hidden = robotData?.hidden_lenders;
    const overdraft = robotData?.overdraft_behaviour;
    const payday = robotData?.payday_survival_days;

    return (
      <div className="space-y-4" data-testid="robot-report-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); setRobotData(null); }} data-testid="back-to-list-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-lg font-semibold flex-1">
            Statement Report {stmt?.bank_name && <span className="text-muted-foreground">({stmt.bank_name.toUpperCase()})</span>}
          </h2>
          {simple && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refreshRobot(selectedId)} disabled={robotLoading} data-testid="refresh-report-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${robotLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={downloadReport} data-testid="download-report-btn">
                <Download className="w-4 h-4 mr-1" /> Download Report
              </Button>
            </div>
          )}
        </div>

        {robotLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : !simple ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              <p>No robot analysis available.</p>
              {stmt?.status === 'uploaded' && (
                <Button className="mt-3" onClick={() => processStatement(selectedId)} disabled={processing} data-testid="process-from-report-btn">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Process Statement
                </Button>
              )}
              {stmt?.status === 'processed' && (
                <Button className="mt-3" onClick={() => loadRobot(selectedId)} data-testid="run-robot-btn">
                  Run Robot Analysis
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Decision Banner */}
            <div className={`rounded-lg p-4 text-center ${DECISION_COLORS[simple.affordability?.decision] || 'bg-gray-600 text-white'}`} data-testid="decision-banner">
              <p className="text-sm opacity-80 uppercase tracking-wide">Decision</p>
              <p className="text-2xl font-bold">{simple.affordability?.decision}</p>
              <p className="text-sm opacity-80 mt-1">Safe to lend: {fmt(simple.affordability?.safe_borrow_amount)}</p>
            </div>

            {/* Identity */}
            <Card data-testid="identity-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4" /> Identity</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{simple.identity?.full_name || '—'}</span></div>
                <div><span className="text-muted-foreground">Address:</span> <span className="font-medium">{simple.identity?.address || '—'}</span></div>
              </CardContent>
            </Card>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="key-metrics-grid">
              <MetricCard label="Salary" value={fmt(simple.salary?.main_salary_amount)} sub={simple.salary?.salary_frequency} icon={DollarSign} />
              <MetricCard label="Lenders" value={simple.lender_detection?.count} sub={`Repayments: ${fmt(simple.lender_detection?.total_repayments)}`} icon={AlertTriangle} warn={simple.lender_detection?.count > 0} />
              <MetricCard label="Gambling" value={simple.gambling?.count} sub={`Spend: ${fmt(simple.gambling?.total_spend)}`} icon={TrendingDown} warn={simple.gambling?.count > 0} />
              <MetricCard label="Avg Balance" value={fmt(simple.balances?.average_balance)} sub={`Lowest: ${fmt(simple.balances?.lowest_balance)}`} icon={BarChart3} warn={simple.balances?.lowest_balance < 0} />
            </div>

            {/* Risk Flags */}
            {riskSummary && (
              <Card data-testid="risk-flags-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Risk Flags (Score: {riskSummary.risk_score}/100)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    <RiskBadge label="Hidden Lenders" risk={hidden?.risk} />
                    <RiskBadge label="Gambling" risk={gambling?.risk} />
                    <RiskBadge label="Overdraft" risk={overdraft?.risk} />
                    <RiskBadge label="Payday Survival" risk={payday?.risk} />
                    <RiskBadge label="Overall" risk={riskSummary.risk_score >= 70 ? 'high' : riskSummary.risk_score >= 40 ? 'medium' : 'low'} />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Income Table - Monthly Breakdown */}
            <IncomeTable data={robotData?.monthly_income_expense} fmt={fmt} />

            {/* Expense Table */}
            <ExpenseTable data={robotData?.monthly_income_expense} fmt={fmt} />

            {/* Monthly Lender Activity */}
            <MonthlyLendersTable data={robotData?.monthly_lenders} fmt={fmt} />

            {/* 3-Month Trend */}
            {trend.length > 0 && (
              <Card data-testid="monthly-trend-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Salary Trend (Last 3 Months)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {trend.map((m, i) => (
                      <div key={m.month} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                        <span className="font-medium">{m.month}</span>
                        <span>{fmt(m.salary)}</span>
                        <span className="text-muted-foreground text-xs">{m.salary_deposits} deposit(s)</span>
                        {i > 0 && trend[i-1] && (
                          <TrendBadge current={m.salary} previous={trend[i-1].salary} />
                        )}
                        {i === 0 && <span className="text-xs text-muted-foreground">latest</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    );
  }

  // ============ LIST + UPLOAD VIEW ============
  return (
    <div className="space-y-4" data-testid="bank-verification-page">
      {/* Upload Form */}
      <Card data-testid="upload-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Upload className="w-4 h-4" /> Upload Statement</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs">Customer Name</Label>
              <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Name" className="h-8 text-sm" data-testid="customer-name-input" />
            </div>
            <div className="w-[140px]">
              <Label className="text-xs">Bank</Label>
              <select value={bankName} onChange={e => setBankName(e.target.value)}
                className="w-full h-8 text-sm border rounded-md px-2 bg-background" data-testid="bank-select">
                <option value="">Auto-detect</option>
                <option value="capitec">Capitec</option>
                <option value="absa">ABSA</option>
                <option value="nedbank">Nedbank</option>
                <option value="fnb">FNB</option>
                <option value="standard_bank">Standard Bank</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">PDF File</Label>
              <Input type="file" accept=".pdf,.csv,.xlsx" className="h-8 text-sm" data-testid="file-input" />
            </div>
            <Button type="submit" disabled={uploading} size="sm" data-testid="upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              <span className="ml-1">Upload</span>
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Statements Table */}
      <Card data-testid="statements-list-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4" /> Statements ({statements.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {statements.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">No statements uploaded yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="statements-table">
                <thead>
                  <tr className="border-b bg-muted/50 text-xs text-muted-foreground">
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Bank</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Decision</th>
                    <th className="text-left p-2">Safe Borrow</th>
                    <th className="text-right p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map(s => (
                    <tr key={s.id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`statement-row-${s.id}`}>
                      <td className="p-2 font-medium">{s.customer_name || '—'}</td>
                      <td className="p-2"><Badge variant="outline" className="text-xs">{(s.bank_name || '?').toUpperCase()}</Badge></td>
                      <td className="p-2">
                        <Badge className={`text-xs ${s.status === 'processed' ? 'bg-emerald-100 text-emerald-700' : s.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                          {s.status}
                        </Badge>
                      </td>
                      <td className="p-2">
                        {s.robot_decision ? (
                          <Badge className={`text-xs ${DECISION_COLORS[s.robot_decision] || ''}`}>{s.robot_decision}</Badge>
                        ) : '—'}
                      </td>
                      <td className="p-2">{s.robot_safe_borrow != null ? fmt(s.robot_safe_borrow) : '—'}</td>
                      <td className="p-2 text-right space-x-1">
                        {s.status === 'uploaded' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => processStatement(s.id)} disabled={processing} data-testid={`process-btn-${s.id}`}>
                            {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Process'}
                          </Button>
                        )}
                        {s.status === 'processed' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openReport(s)} data-testid={`view-btn-${s.id}`}>
                            <Eye className="w-3 h-3 mr-1" /> Report
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => deleteStatement(s.id)} data-testid={`delete-btn-${s.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============ Small Helper Components ============

function MetricCard({ label, value, sub, icon: Icon, warn }) {
  return (
    <Card className={warn ? 'border-amber-300' : ''}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`w-4 h-4 ${warn ? 'text-amber-500' : 'text-muted-foreground'}`} />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <p className="text-lg font-bold">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function RiskBadge({ label, risk }) {
  const r = risk || 'unknown';
  return (
    <div className={`rounded px-2 py-1 text-center ${RISK_BADGE[r] || RISK_BADGE.unknown}`}>
      <div className="font-medium">{label}</div>
      <div className="uppercase">{r}</div>
    </div>
  );
}

function TrendBadge({ current, previous }) {
  if (!previous || previous === 0) return null;
  const pct = ((current - previous) / previous * 100).toFixed(0);
  const up = current >= previous;
  return (
    <span className={`text-xs font-medium ${up ? 'text-emerald-600' : 'text-red-600'}`}>
      {up ? '+' : ''}{pct}%
    </span>
  );
}



const INCOME_ROWS = [
  { key: 'fixed_monthly_gross_salary', label: 'Fixed Monthly Gross salary (payslip)' },
  { key: 'average_monthly_overtime', label: 'Average monthly overtime' },
  { key: 'other_monthly_income', label: 'Other monthly income' },
  { key: 'contribution_from_family_member', label: 'Contribution from family member' },
  { key: 'spouse_income', label: 'Spouse income. For "Joint consumers" credit.' },
];

const DEDUCTION_ROWS = [
  { key: 'paye', label: 'PAYE:' },
  { key: 'uif', label: 'UIF:' },
  { key: 'other_payslip_deductions', label: 'Other Payslip Deductions:' },
];

const EXPENSE_ROWS = [
  { key: 'property_rental', label: 'Property Rental' },
  { key: 'utilities_water_electricity', label: 'Utilities (Water / Electricity)' },
  { key: 'groceries', label: 'Groceries' },
  { key: 'transport_fuel', label: 'Transport / Fuel' },
  { key: 'communication_airtime_data_wifi', label: 'Communication / Cellphone Airtime / Data / Wifi' },
  { key: 'school_fees', label: 'School Fees' },
  { key: 'childcare_creche', label: 'Childcare / Creche' },
  { key: 'medical_aid', label: 'Medical Aid' },
  { key: 'medication', label: 'Medication' },
  { key: 'debt_repayments_not_listed_on_credit_bureau', label: 'Debt Repayments - Not listed on Credit Bureau' },
  { key: 'insurance_funeral_car_household', label: 'Insurance - Funeral / Car / Household' },
  { key: 'entertainment', label: 'Entertainment' },
  { key: 'clothing_footwear', label: 'Clothing / Footwear' },
  { key: 'savings_stokvel_contributions', label: 'Savings / Stokvel Contributions' },
  { key: 'household_maintenance', label: 'Household Maintenance' },
  { key: 'other_expense', label: 'Other' },
];

function formatMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const names = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[parseInt(m)] || m} ${y}`;
}

function AmtCell({ value, bold, highlight }) {
  const v = Number(value || 0).toFixed(2);
  return (
    <td className={`text-right px-2 py-1.5 ${bold ? 'font-bold' : ''} ${highlight ? 'bg-gray-200' : ''}`}>
      <span className="inline-block border border-gray-300 rounded px-2 py-0.5 min-w-[70px] text-right text-sm bg-white">
        {v}
      </span>
    </td>
  );
}

function IncomeTable({ data, fmt }) {
  if (!data || !data.months) return null;
  const months = data.months || [];
  const avg = data.average || {};

  return (
    <Card data-testid="income-table-card">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="income-table">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2 font-bold text-gray-700 min-w-[250px]">Income</th>
              {months.map(m => (
                <th key={m.month} className="text-right p-2 font-bold text-gray-700 min-w-[110px]">{formatMonth(m.month)}</th>
              ))}
              <th className="text-right p-2 font-bold text-gray-700 min-w-[110px]">Average Income</th>
            </tr>
          </thead>
          <tbody>
            {INCOME_ROWS.map(row => (
              <tr key={row.key} className="border-b border-gray-100">
                <td className="p-2 text-gray-600">{row.label}</td>
                {months.map(m => <AmtCell key={m.month} value={m.income?.[row.key]} />)}
                <AmtCell value={avg.income?.[row.key]} />
              </tr>
            ))}
            {/* TOTAL GROSS */}
            <tr className="border-b border-gray-300 bg-gray-50">
              <td className="p-2 font-bold text-right text-gray-700">TOTAL: GROSS MONTHLY INCOME:</td>
              {months.map(m => <AmtCell key={m.month} value={m.gross_monthly_income} bold />)}
              <AmtCell value={avg.gross_monthly_income} bold />
            </tr>
            {/* Deductions */}
            {DEDUCTION_ROWS.map(row => (
              <tr key={row.key} className="border-b border-gray-100">
                <td className="p-2 text-gray-600 text-right">{row.label}</td>
                {months.map(m => <AmtCell key={m.month} value={m.deductions?.[row.key]} />)}
                <AmtCell value={avg.deductions?.[row.key]} />
              </tr>
            ))}
            {/* TOTAL TAKE HOME */}
            <tr className="border-t-2 border-gray-400 bg-gray-100">
              <td className="p-2 font-bold text-right text-gray-800">TOTAL TAKE HOME INCOME:</td>
              {months.map(m => <AmtCell key={m.month} value={m.total_take_home_income} bold highlight />)}
              <AmtCell value={avg.total_take_home_income} bold highlight />
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ExpenseTable({ data, fmt }) {
  if (!data || !data.months) return null;
  const months = data.months || [];
  const avg = data.average || {};

  // Use average for a single "Amount" column. If 3 months available, show all 3 + average
  const showMonthly = months.length > 1;

  return (
    <Card data-testid="expense-table-card">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="expense-table">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2 font-bold text-gray-700 min-w-[300px]">Expense</th>
              {showMonthly ? (
                <>
                  {months.map(m => (
                    <th key={m.month} className="text-right p-2 font-bold text-gray-700 min-w-[110px]">{formatMonth(m.month)}</th>
                  ))}
                  <th className="text-right p-2 font-bold text-gray-700 min-w-[110px]">Average</th>
                </>
              ) : (
                <th className="text-right p-2 font-bold text-gray-700 min-w-[110px]">Amount</th>
              )}
            </tr>
          </thead>
          <tbody>
            {EXPENSE_ROWS.map(row => (
              <tr key={row.key} className="border-b border-gray-100">
                <td className="p-2 text-gray-600">{row.label}</td>
                {showMonthly ? (
                  <>
                    {months.map(m => <AmtCell key={m.month} value={m.expenses?.[row.key]} />)}
                    <AmtCell value={avg.expenses?.[row.key]} />
                  </>
                ) : (
                  <AmtCell value={avg.expenses?.[row.key] || months[0]?.expenses?.[row.key]} />
                )}
              </tr>
            ))}
            {/* TOTAL */}
            <tr className="border-t-2 border-gray-400">
              <td className="p-2"></td>
              {showMonthly ? (
                <>
                  {months.map(m => <AmtCell key={m.month} value={m.total_expenses} bold highlight />)}
                  <AmtCell value={avg.total_expenses} bold highlight />
                </>
              ) : (
                <AmtCell value={avg.total_expenses || months[0]?.total_expenses} bold highlight />
              )}
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}


function MonthlyLendersTable({ data, fmt }) {
  if (!data || data.length === 0) return null;

  return (
    <Card data-testid="monthly-lenders-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Lender Activity by Month
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.map((monthData) => (
          <div key={monthData.month} className="border rounded-lg overflow-hidden">
            <div className="bg-muted/60 px-3 py-2 flex items-center justify-between border-b">
              <span className="font-semibold text-sm">{formatMonth(monthData.month)}</span>
              <div className="flex gap-3 text-xs">
                <span className="text-emerald-700">Gave: {fmt(monthData.total_gave)}</span>
                <span className="text-red-700">Took: {fmt(monthData.total_took)}</span>
              </div>
            </div>
            {monthData.lenders.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No lender activity</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground bg-muted/30">
                    <th className="text-left px-3 py-1.5">Lender</th>
                    <th className="text-center px-3 py-1.5">Active</th>
                    <th className="text-right px-3 py-1.5">Gave (Credit)</th>
                    <th className="text-right px-3 py-1.5">Took (Debit)</th>
                    <th className="text-right px-3 py-1.5">Txns</th>
                  </tr>
                </thead>
                <tbody>
                  {monthData.lenders.map((lender, i) => (
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-1.5 font-medium">{lender.name}</td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`inline-block w-2 h-2 rounded-full ${lender.active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                      </td>
                      <td className="px-3 py-1.5 text-right text-emerald-700">
                        {lender.gave > 0 ? fmt(lender.gave) : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right text-red-700">
                        {lender.took > 0 ? fmt(lender.took) : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right text-muted-foreground">{lender.transaction_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
